import { Icon } from './Icons';
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { apiPost } from '../api';

// ── Helpers ───────────────────────────────────────────────────────────────────

async function loadCompPlan(type) {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'compPlan_' + type));
  return snap.exists() ? snap.data() : null;
}

async function saveCompPlan(type, data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'compPlan_' + type), { ...data, savedAt: serverTimestamp() });
}

// ── Plan configs ──────────────────────────────────────────────────────────────

const PLANS = {
  marathon: {
    id: 'marathon',
    label: 'Marathon',
    distance: '26.2 miles / 42.2km',
    icon: 'activity',
    color: '#f0a050',
    weeks: 16,
    description: 'A 16-week build from base mileage to race day, including tempo runs, long runs, speed work, strength sessions and taper.',
    keyWorkouts: ['Long run (Sunday)', 'Tempo run (Tuesday)', 'Interval session (Thursday)', 'Easy recovery run (Wednesday)', 'Strength + core (Monday)', 'Rest or cross-train (Friday)', 'Shakeout run (Saturday)'],
  },
  half_marathon: {
    id: 'half_marathon',
    label: 'Half Marathon',
    distance: '13.1 miles / 21.1km',
    icon: 'activity',
    color: '#00AEEF',
    weeks: 12,
    description: 'A 12-week progressive plan building aerobic base, lactate threshold and race-specific fitness with integrated strength and core work.',
    keyWorkouts: ['Long run (Sunday)', 'Tempo run (Wednesday)', 'Speed intervals (Tuesday)', 'Easy run (Thursday)', 'Strength + core (Monday)', 'Rest (Friday)', 'Parkrun / race pace (Saturday)'],
  },
  hyrox: {
    id: 'hyrox',
    label: 'Hyrox',
    distance: '8 x 1km runs + 8 functional stations',
    icon: 'zap',
    color: '#22c55e',
    weeks: 12,
    description: 'A 12-week hybrid fitness plan combining running endurance, strength and the 8 Hyrox functional movements: SkiErg, sled push, sled pull, burpee broad jumps, rowing, farmers carry, sandbag lunges and wall balls.',
    keyWorkouts: ['Long run + functional work (Sunday)', 'Running intervals (Tuesday)', 'Hyrox simulation workout (Thursday)', 'Strength (Monday)', 'Skill practice — sleds, carries (Wednesday)', 'Core + mobility (Friday)', 'Rest (Saturday)'],
  },
  century_ride: {
    id: 'century_ride',
    label: '100km Cycle',
    distance: '100km / 62 miles',
    icon: 'trending-up',
    color: '#a78bfa',
    weeks: 10,
    description: 'A 10-week cycling plan building aerobic base, climbing ability and pacing strategy with supporting strength and core sessions.',
    keyWorkouts: ['Long ride (Sunday)', 'Tempo intervals on bike (Wednesday)', 'Climbing repeats (Saturday)', 'Strength + legs (Monday)', 'Easy spin / recovery (Tuesday)', 'Core + hip stability (Thursday)', 'Rest (Friday)'],
  },
};

// ── UI Components ─────────────────────────────────────────────────────────────

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem', ...style }}>{children}</div>;
}

function Btn({ children, onClick, disabled, variant = 'primary', full = false, style = {} }) {
  const variants = {
    primary: { background: 'var(--primary)', color: '#080f18', border: 'none' },
    secondary: { background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)' },
    success: { background: 'var(--success)', color: '#080f18', border: 'none' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding: '.65rem 1.25rem', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .6 : 1, transition: 'all .2s', width: full ? '100%' : 'auto', ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none' }}>
        <option value=''>Select…</option>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
}

// ── Week Plan View ────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function normaliseSession(session, index) {
  let dayLabel = session.day;
  if (typeof session.day === 'number') {
    dayLabel = DAY_NAMES[session.day] || `Day ${session.day}`;
  }
  const details = session.details || session.workout || session.content || {};
  const name = session.name || session.title || session.sessionName || details.name || details.title || `Session ${index + 1}`;
  const description = session.description || session.notes || session.overview || details.description || details.notes || '';
  const type = session.type || session.sessionType || session.category || details.type || '';
  const coachNote = session.coachNote || session.note || session.tip || details.coachNote || '';
  const exercises = session.exercises || session.workoutDetails || details.exercises || [];
  const coreWork = session.coreWork || session.core || session.coreBlock || details.coreWork || [];
  const cardio = session.cardio || session.cardioFinisher || details.cardio || null;
  const duration = session.duration || session.time || details.duration || '';
  const distance = session.distance || details.distance || '';
  const pace = session.pace || session.paceTarget || details.pace || '';

  return { dayLabel, name, description, type, coachNote, exercises, coreWork, cardio, duration, distance, pace };
}

function SessionCard({ session, index, planConfig }) {
  const [expanded, setExpanded] = useState(false);
  const s = normaliseSession(session, index);

  const sessionTypeColor = (type) => {
    if (!type) return 'var(--text3)';
    const t = type.toLowerCase();
    if (t.includes('long') || t.includes('run')) return planConfig.color;
    if (t.includes('strength') || t.includes('gym') || t.includes('weight')) return 'var(--warning)';
    if (t.includes('interval') || t.includes('speed') || t.includes('tempo')) return '#22c55e';
    if (t.includes('core') || t.includes('mobility') || t.includes('stretch')) return '#a78bfa';
    if (t.includes('rest') || t.includes('recovery') || t.includes('off')) return 'var(--text3)';
    if (t.includes('hyrox') || t.includes('functional') || t.includes('circuit')) return '#22c55e';
    if (t.includes('easy') || t.includes('zone')) return planConfig.color;
    if (t.includes('cardio') || t.includes('bike') || t.includes('swim') || t.includes('row')) return '#00c8a0';
    return 'var(--primary)';
  };

  const color = sessionTypeColor(s.type);
  const isRest = s.type && (s.type.toLowerCase().includes('rest') || s.type.toLowerCase().includes('off'));

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: '.6rem' }}>
      <div onClick={() => !isRest && setExpanded(v => !v)}
        style={{ padding: '1rem 1.25rem', cursor: isRest ? 'default' : 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}
        onMouseEnter={e => { if (!isRest) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.65rem', marginBottom: '.3rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--text3)', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', padding: '.15rem .55rem', borderRadius: 20, flexShrink: 0 }}>{s.dayLabel}</span>
            <span style={{ fontSize: '.88rem', fontWeight: 500, color: isRest ? 'var(--text3)' : 'var(--text)' }}>{s.name}</span>
            {s.duration && <span style={{ fontSize: '.68rem', color: 'var(--text3)' }}>{s.duration}</span>}
            {s.distance && <span style={{ fontSize: '.68rem', color: planConfig.color, fontWeight: 500 }}>{s.distance}</span>}
          </div>
          {s.description && (
            <div style={{ fontSize: '.75rem', color: 'var(--text3)', lineHeight: 1.6, fontWeight: 300 }}>{s.description}</div>
          )}
          {s.pace && (
            <div style={{ fontSize: '.72rem', color: '#22c55e', marginTop: '.3rem' }}>Target pace: {s.pace}</div>
          )}
          {/* Indicators */}
          {!isRest && (s.exercises?.length > 0 || s.coreWork?.length > 0 || s.cardio) && (
            <div style={{ display: 'flex', gap: '.4rem', marginTop: '.5rem', flexWrap: 'wrap' }}>
              {s.exercises?.length > 0 && <span style={{ fontSize: '.62rem', padding: '.15rem .5rem', borderRadius: 20, background: 'var(--bg-elevated)', color: 'var(--text3)', border: '1px solid var(--border2)' }}>{s.exercises.length} exercises</span>}
              {s.coreWork?.length > 0 && <span style={{ fontSize: '.62rem', padding: '.15rem .5rem', borderRadius: 20, background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}>{s.coreWork.length} core</span>}
              {s.cardio && <span style={{ fontSize: '.62rem', padding: '.15rem .5rem', borderRadius: 20, background: 'rgba(0,200,150,0.08)', color: 'var(--success)', border: '1px solid rgba(0,200,150,0.2)' }}>cardio</span>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexShrink: 0 }}>
          <span style={{ fontSize: '.68rem', padding: '.2rem .65rem', borderRadius: 20, background: color + '15', color, fontWeight: 600, border: '1px solid ' + color + '30' }}>
            {s.type || 'Session'}
          </span>
          {!isRest && <span style={{ fontSize: '.7rem', color: 'var(--text3)' }}>{expanded ? '▲' : '▼'}</span>}
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--border2)', background: 'var(--bg-elevated)' }}>
          {s.coachNote && (
            <div style={{ padding: '.75rem 1.25rem', borderBottom: '1px solid var(--border2)', fontSize: '.75rem', color: 'var(--primary)', lineHeight: 1.6, fontStyle: 'italic' }}>
              {s.coachNote}
            </div>
          )}

          {/* Main exercises */}
          {s.exercises && s.exercises.length > 0 && (
            <div style={{ padding: '.75rem 1.25rem', borderBottom: s.coreWork?.length > 0 || s.cardio ? '1px solid var(--border2)' : 'none' }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>Main Work</div>
              {s.exercises.map((ex, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.3rem 0', borderBottom: i < s.exercises.length - 1 ? '1px solid var(--border2)' : 'none', display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontWeight: 300 }}>
                  <span style={{ color, flexShrink: 0, marginTop: '.1rem' }}>—</span>
                  <span>{typeof ex === 'string' ? ex : (ex.name || ex.exercise || ex.description || JSON.stringify(ex))}</span>
                </div>
              ))}
            </div>
          )}

          {/* Core work */}
          {s.coreWork && s.coreWork.length > 0 && (
            <div style={{ padding: '.75rem 1.25rem', borderBottom: s.cardio ? '1px solid var(--border2)' : 'none' }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.5rem' }}>Core Work</div>
              {s.coreWork.map((ex, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.3rem 0', borderBottom: i < s.coreWork.length - 1 ? '1px solid var(--border2)' : 'none', display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontWeight: 300 }}>
                  <span style={{ color: '#a78bfa', flexShrink: 0, marginTop: '.1rem' }}>—</span>
                  <span>{typeof ex === 'string' ? ex : (ex.name || ex.description || JSON.stringify(ex))}</span>
                </div>
              ))}
            </div>
          )}

          {/* Cardio */}
          {s.cardio && (
            <div style={{ padding: '.75rem 1.25rem' }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.4rem' }}>Cardio Finisher</div>
              <div style={{ fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.6 }}>
                {typeof s.cardio === 'string' ? s.cardio : s.cardio.description || JSON.stringify(s.cardio)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WeekView({ plan, planConfig }) {
  const [selectedWeek, setSelectedWeek] = useState(1);
  const weeks = plan.weeks || [];
  const week = weeks.find(w => w.week === selectedWeek) || weeks[0];

  return (
    <div>
      <div style={{ display: 'flex', gap: '.3rem', overflowX: 'auto', paddingBottom: '.4rem', marginBottom: '1.25rem', scrollbarWidth: 'none' }}>
        {weeks.map(w => (
          <button key={w.week} onClick={() => setSelectedWeek(w.week)} style={{
            padding: '.4rem .75rem', borderRadius: 'var(--radius)', border: '1px solid', flexShrink: 0,
            borderColor: selectedWeek === w.week ? planConfig.color : 'var(--border2)',
            background: selectedWeek === w.week ? planConfig.color + '18' : 'transparent',
            color: selectedWeek === w.week ? planConfig.color : 'var(--text3)',
            fontSize: '.72rem', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: selectedWeek === w.week ? 600 : 400,
          }}>
            <div>Wk {w.week}</div>
            {w.isTaper && <div style={{ fontSize: '.58rem', color: 'var(--warning)' }}>Taper</div>}
            {w.isRace && <div style={{ fontSize: '.58rem', color: planConfig.color }}>Race</div>}
          </button>
        ))}
      </div>

      {week && (
        <div>
          <div style={{ padding: '.75rem 1.1rem', background: planConfig.color + '10', border: '1px solid ' + planConfig.color + '30', borderRadius: 'var(--radius-lg)', marginBottom: '1rem' }}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '1rem', color: planConfig.color, marginBottom: '.2rem' }}>
              Week {week.week}{week.phase ? ` — ${week.phase}` : ''}{week.isTaper ? ' — TAPER' : ''}{week.isRace ? ' — RACE WEEK' : ''}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '.75rem', color: 'var(--text3)', flexWrap: 'wrap' }}>
              {week.totalDistance && <span><Icon name="activity" size={13} /> {week.totalDistance}</span>}
              {week.totalTime && <span><Icon name="clock" size={13} /> {week.totalTime}</span>}
              {week.focus && <span><Icon name="target" size={13} /> {week.focus}</span>}
            </div>
          </div>
          <div>
            {(week.sessions || []).map((session, i) => (
              <SessionCard key={i} session={session} index={i} planConfig={planConfig} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Setup Form ────────────────────────────────────────────────────────────────

function PlanSetupForm({ planConfig, healthData, onGenerated, onCancel }) {
  const [raceDate, setRaceDate] = useState('');
  const [currentFitness, setCurrentFitness] = useState('');
  const [weeklyVolume, setWeeklyVolume] = useState('');
  const [goal, setGoal] = useState('');
  const [injuries, setInjuries] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const fitnessOptions = {
    marathon: ['Complete beginner (under 20 miles/week)', 'Building base (20-30 miles/week)', 'Solid base (30-40 miles/week)', 'Experienced (40+ miles/week)'],
    half_marathon: ['Complete beginner (under 15 miles/week)', 'Some running background (15-20 miles/week)', 'Regular runner (20-30 miles/week)', 'Experienced (30+ miles/week)'],
    hyrox: ['Gym-based, new to running', 'Runner, new to functional fitness', 'Crossfit / functional fitness background', 'Previous Hyrox competitor'],
    century_ride: ['New to cycling (under 50km/week)', 'Recreational cyclist (50-100km/week)', 'Regular cyclist (100-150km/week)', 'Experienced cyclist (150km+ /week)'],
  };

  const goalOptions = {
    marathon: ['Just finish (sub 6hrs)', 'Under 5 hours', 'Under 4:30', 'Under 4 hours', 'Under 3:30', 'Sub 3 hours'],
    half_marathon: ['Just finish', 'Under 2:30', 'Under 2 hours', 'Under 1:45', 'Under 1:30'],
    hyrox: ['Just finish and enjoy it', 'Under 2 hours', 'Under 1:30', 'Under 1:15', 'Podium / competitive'],
    century_ride: ['Just complete it', 'Under 5 hours', 'Under 4 hours', 'Under 3:30'],
  };

  async function handleGenerate() {
    if (!currentFitness || !goal) { setError('Please fill in your current fitness and goal.'); return; }
    setGenerating(true);
    setError('');

    let healthContext = '';
    if (healthData?.blood?.systolicBP && parseFloat(healthData.blood.systolicBP) >= 140) healthContext += 'CLINICAL FLAG: Elevated BP — cap intensity, include HR monitoring guidance.\n';
    if (healthData?.blood?.vitaminD && parseFloat(healthData.blood.vitaminD) < 30) healthContext += 'CLINICAL FLAG: Low Vitamin D — note injury risk, include supplementation note.\n';
    if (healthData?.mental?.stressScore && parseFloat(healthData.mental.stressScore) >= 70) healthContext += 'CLINICAL FLAG: High stress score — include recovery emphasis, reduce intensity in high-stress weeks.\n';
    if (healthData?.blood?.ferritin && parseFloat(healthData.blood.ferritin) < 30) healthContext += 'CLINICAL FLAG: Low ferritin — monitor fatigue, flag dietary iron needs.\n';

    const planDescriptions = {
      marathon: 'marathon (26.2 miles / 42.2km) — 16 weeks, peak long run 20-22 miles, 3-week taper',
      half_marathon: 'half marathon (13.1 miles / 21.1km) — 12 weeks, peak long run 13-14 miles, 2-week taper',
      hyrox: 'Hyrox (8x1km runs + 8 functional stations) — 12 weeks, build to full Hyrox simulation week 10',
      century_ride: '100km cycle — 10 weeks, peak long ride 80-90km',
    };

    const half = Math.ceil(planConfig.weeks / 2);
    const weeks1 = Array.from({length: half}, (_, i) => i + 1);
    const weeks2 = Array.from({length: planConfig.weeks - half}, (_, i) => i + half + 1);

    function buildHalfPrompt(weekNums) {
      return `Elite endurance coach. Build weeks ${weekNums[0]}-${weekNums[weekNums.length-1]} of a ${planDescriptions[planConfig.id]} plan.

Athlete: ${currentFitness} | Goal: ${goal} | Injuries: ${injuries || 'None'}${healthContext ? ' | ' + healthContext.trim() : ''}

Rules: Every session needs exercises[], coreWork[] (2 items), cardio string. Keep all strings short.

Return ONLY a JSON array, no markdown:
[{"week":${weekNums[0]},"phase":"Base","isTaper":false,"isRace":false,"focus":"Aerobic base","sessions":[{"day":"Mon","name":"Strength","type":"Strength","exercises":["Squat 4x8","RDL 3x10"],"coreWork":["Plank 3x45s","Dead bug 3x10e"],"cardio":"10min bike Z2","coachNote":"Moderate only"}]}]

Build ALL weeks: ${weekNums.join(',')}. Return ONLY the JSON array.`;
    }

    function repairJSON(str) {
      const start = str.indexOf('[');
      if (start === -1) return null;
      let s = str.slice(start);
      let stack = [];
      for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '[' || ch === '{') stack.push(ch === '[' ? ']' : '}');
        else if (ch === ']' || ch === '}') {
          if (stack.length && stack[stack.length-1] === ch) stack.pop();
        }
        if (stack.length === 0 && i > 0) return s.slice(0, i + 1);
      }
      // Truncated — try to repair
      let repaired = s.trimEnd().replace(/,\s*$/, '');
      // Remove incomplete last string/object
      const lastBrace = Math.max(repaired.lastIndexOf('}'), repaired.lastIndexOf(']'));
      if (lastBrace > 0) repaired = repaired.slice(0, lastBrace + 1);
      while (stack.length > 0) repaired += stack.pop();
      try { JSON.parse(repaired); return repaired; } catch(e) { return null; }
    }

    async function generateHalf(weekNums) {
      const data = await apiPost('/api/pt', {
        messages: [{ role: 'user', content: buildHalfPrompt(weekNums) }],
        trainingProfile: null, liftLog: '', workingWeights: '', healthData
      });
      const raw = data.content || '';
      const arrMatch = raw.match(/\[[\s\S]*\]/);
      if (!arrMatch) throw new Error('No JSON array in response for weeks ' + weekNums[0] + '-' + weekNums[weekNums.length-1]);
      try {
        return JSON.parse(arrMatch[0]);
      } catch(e) {
        const repaired = repairJSON(arrMatch[0]);
        if (repaired) return JSON.parse(repaired);
        throw new Error('JSON parse failed for weeks ' + weekNums[0] + '-' + weekNums[weekNums.length-1] + ': ' + e.message);
      }
    }

    try {
      const [half1, half2] = await Promise.all([generateHalf(weeks1), generateHalf(weeks2)]);
      const allWeeks = [...half1, ...half2].sort((a, b) => (a.week||0) - (b.week||0));
      const plan = {
        planTitle: `${planConfig.label} Plan — ${goal}`,
        event: planConfig.label,
        goal,
        totalWeeks: planConfig.weeks,
        weeks: allWeeks,
        raceDayStrategy: 'Start conservatively. Split the race in thirds — hold back early, settle into race pace, finish strong.',
        nutritionNotes: 'Carb-load 2-3 days before. Race morning: familiar meal 2-3hrs prior. Nutrition every 45 min during event.',
        keyPrinciples: ['Consistency over intensity', 'Easy days must be genuinely easy', 'Core and strength prevent injury']
      };
      await saveCompPlan(planConfig.id, { plan, currentFitness, goal, raceDate, generatedAt: new Date().toISOString() });
      onGenerated(plan);
    } catch (e) {
      console.error(e);
      setError('Generation failed: ' + e.message + '. Please try again.');
    }
    setGenerating(false);
  }


  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.4rem' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: planConfig.color + '18', border: '1px solid ' + planConfig.color + '40', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={planConfig.icon} size={18} color={planConfig.color} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)' }}>{planConfig.label} Plan</h2>
        </div>
        <p style={{ fontSize: '.8rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.65 }}>{planConfig.description}</p>
      </div>

      <Card>
        <div style={{ fontSize: '.65rem', fontWeight: 600, color: planConfig.color, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Your Details</div>
        <Inp label="Race date (optional)" type="date" value={raceDate} onChange={setRaceDate} />
        <Sel label="Current fitness level" value={currentFitness} onChange={setCurrentFitness} options={fitnessOptions[planConfig.id]} />
        <Inp label="Current weekly training volume (optional)" value={weeklyVolume} onChange={setWeeklyVolume} placeholder={planConfig.id === 'century_ride' ? 'e.g. 80km/week' : 'e.g. 25 miles/week'} />
        <Sel label="Race goal" value={goal} onChange={setGoal} options={goalOptions[planConfig.id]} />
        <Inp label="Injuries or limitations (optional)" value={injuries} onChange={setInjuries} placeholder="e.g. Left knee niggle, avoid hill repeats" />

        {healthData && (
          <div style={{ padding: '.75rem 1rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6, marginTop: '.5rem' }}>
            <Icon name="info" size={14} /> Rico will factor in your health data — blood markers, stress scores, and any clinical flags — when building your plan.
          </div>
        )}
      </Card>

      {error && <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>{error}</div>}

      {generating ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(0,174,239,0.2)', borderTop: `3px solid ${planConfig.color}`, animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <div style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Building your {planConfig.weeks}-week plan… (~30 seconds)</div>
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '.75rem' }}>
          <Btn onClick={handleGenerate} style={{ background: planConfig.color, color: '#080f18' }}>Generate My Plan →</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CompetitionPlans({ healthData }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [setupMode, setSetupMode] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSelectPlan(planId) {
    setLoading(true);
    setSelectedPlan(planId);
    const existing = await loadCompPlan(planId);
    if (existing?.plan) {
      setGeneratedPlan(existing.plan);
      setSetupMode(false);
    } else {
      setGeneratedPlan(null);
      setSetupMode(true);
    }
    setLoading(false);
  }

  const planConfig = PLANS[selectedPlan];

  if (loading) return <div style={{ padding: '2rem 0', color: 'var(--text3)', fontSize: '.82rem' }}>Loading…</div>;

  // Plan selection screen
  if (!selectedPlan) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ marginBottom: '1.75rem' }}>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.3rem' }}>Competition Plans</h2>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>AI-generated event-specific plans built around your health data. Includes cardio, strength and core every week.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {Object.values(PLANS).map(plan => (
            <div key={plan.id} onClick={() => handleSelectPlan(plan.id)}
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', cursor: 'pointer', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = plan.color; e.currentTarget.style.background = plan.color + '08'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.background = 'var(--bg-card)'; }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: plan.color + '15', border: '1px solid ' + plan.color + '35', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <Icon name={plan.icon} size={22} color={plan.color} />
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.3rem' }}>{plan.label}</div>
              <div style={{ fontSize: '.75rem', color: plan.color, fontWeight: 500, marginBottom: '.6rem' }}>{plan.distance}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text3)', lineHeight: 1.6, fontWeight: 300, marginBottom: '1rem' }}>{plan.description}</div>
              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 20, background: plan.color + '15', color: plan.color, fontWeight: 600 }}>{plan.weeks} weeks</span>
                <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 20, background: 'var(--bg-elevated)', color: 'var(--text3)', border: '1px solid var(--border2)' }}>Cardio + Core included</span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '1.5rem', padding: '1rem 1.25rem', background: 'rgba(0,174,239,0.05)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius-lg)', fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.65 }}>
          <Icon name="info" size={14} /> All competition plans are generated by Rico using your health data, blood markers and current fitness level. Plans include integrated strength, core and cardio sessions every week — not just the sport itself.
        </div>
      </div>
    );
  }

  // Setup form
  if (setupMode) {
    return (
      <PlanSetupForm
        planConfig={planConfig}
        healthData={healthData}
        onGenerated={(plan) => { setGeneratedPlan(plan); setSetupMode(false); }}
        onCancel={() => { setSelectedPlan(null); setSetupMode(false); }}
      />
    );
  }

  // Generated plan view
  if (generatedPlan) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.3rem' }}>
              <Icon name={planConfig.icon} size={18} color={planConfig.color} />
              <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)' }}>{generatedPlan.planTitle || planConfig.label + ' Plan'}</h2>
            </div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>{generatedPlan.event} · Goal: {generatedPlan.goal} · {generatedPlan.totalWeeks} weeks</p>
          </div>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <Btn variant="secondary" onClick={() => { setSetupMode(true); setGeneratedPlan(null); }}>Regenerate</Btn>
            <Btn variant="secondary" onClick={() => setSelectedPlan(null)}>← All Plans</Btn>
          </div>
        </div>

        {/* Key principles */}
        {generatedPlan.keyPrinciples && generatedPlan.keyPrinciples.length > 0 && (
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
            {generatedPlan.keyPrinciples.map((p, i) => (
              <div key={i} style={{ fontSize: '.72rem', padding: '.3rem .75rem', borderRadius: 20, background: planConfig.color + '12', border: '1px solid ' + planConfig.color + '30', color: planConfig.color, fontWeight: 400 }}>{p}</div>
            ))}
          </div>
        )}

        <WeekView plan={generatedPlan} planConfig={planConfig} />

        {/* Race day strategy */}
        {generatedPlan.raceDayStrategy && (
          <Card style={{ marginTop: '1rem', background: planConfig.color + '06', borderColor: planConfig.color + '25' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: planConfig.color, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Race Day Strategy</div>
            <p style={{ fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.7 }}>{generatedPlan.raceDayStrategy}</p>
          </Card>
        )}

        {/* Nutrition notes */}
        {generatedPlan.nutritionNotes && (
          <Card style={{ background: 'rgba(240,160,80,0.04)', borderColor: 'rgba(240,160,80,0.2)' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Nutrition Notes</div>
            <p style={{ fontSize: '.82rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.7 }}>{generatedPlan.nutritionNotes}</p>
          </Card>
        )}
      </div>
    );
  }

  return null;
}
